import { format } from "date-fns";
import { Calendar as CalendarIcon } from "lucide-react";
import { strings } from "@/I18n";
import { cn } from "@/lib/utils";
import { Button } from "@/v2/ui/primitives/button";
import { Calendar } from "@/v2/ui/primitives/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/v2/ui/primitives/popover";

interface DatePickerProps {
  date?: Date | null;
  onDateChange?: (date: Date | null) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  /**
   * When true, the calendar caption renders month + year as dropdown selects
   * instead of prev/next arrows only. Lets a user jump from "May 2026" to
   * "March 2024" in two clicks — needed for billing-report flows where the
   * partner picks a specific month several scrolls back. Pairs with
   * `fromYear` / `toYear` to bound the year dropdown.
   */
  monthYearDropdown?: boolean;
  fromYear?: number;
  toYear?: number;
  /** Disables dates before this (passed straight through to react-day-picker). */
  fromDate?: Date;
  /** Disables dates after this. */
  toDate?: Date;
  /** Compact trigger button sizing, e.g. for inline use in toolbars. */
  size?: "default" | "sm";
  /** Optional id for the trigger button — wires up label-for / aria. */
  id?: string;
  /** Optional data-testid plumbed onto the trigger. */
  testId?: string;
}

export function DatePicker({
  date,
  onDateChange,
  placeholder = strings("common.pickDate"),
  className,
  disabled = false,
  monthYearDropdown = false,
  fromYear,
  toYear,
  fromDate,
  toDate,
  size = "default",
  id,
  testId,
}: DatePickerProps) {
  const handleSelect = (selectedDate: Date | undefined) => {
    onDateChange?.(selectedDate ?? null);
  };

  // Default year range when dropdown is enabled: ±5 years around today.
  // Wide enough for back-month billing and forward planning; narrow enough that
  // the dropdown stays scannable.
  const now = new Date();
  const resolvedFromYear = fromYear ?? now.getFullYear() - 5;
  const resolvedToYear = toYear ?? now.getFullYear() + 1;

  return (
    <Popover>
      <PopoverTrigger asChild={true}>
        <Button
          id={id}
          data-testid={testId}
          variant={"outline"}
          className={cn(
            "tw-w-full tw-justify-start tw-text-left tw-font-normal",
            size === "default" ? "tw-h-10" : "tw-h-8 tw-text-sm",
            !date && "tw-text-muted-foreground",
            className
          )}
          disabled={disabled}
        >
          <CalendarIcon className="tw-mr-2 tw-h-4 tw-w-4" />
          {date ? format(date, "PPP") : <span>{placeholder}</span>}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="tw-w-auto tw-p-0">
        <Calendar
          mode="single"
          selected={date || undefined}
          onSelect={handleSelect}
          initialFocus={true}
          captionLayout={monthYearDropdown ? "dropdown" : undefined}
          startMonth={
            monthYearDropdown ? new Date(resolvedFromYear, 0, 1) : undefined
          }
          endMonth={
            monthYearDropdown ? new Date(resolvedToYear, 11, 31) : undefined
          }
          defaultMonth={date ?? undefined}
          // Build the DateInterval matcher conditionally: passing
          // `undefined` where react-day-picker expects a `Date` (which an
          // `as Date` cast would silently allow) throws at the first
          // calendar render that hits the matcher. Today every caller
          // here passes both bounds, but the prop signature allows
          // either alone (e.g. a future "disable past dates only" use
          // case with no upper ceiling) — keep that path safe.
          disabled={
            fromDate && toDate
              ? { before: fromDate, after: toDate }
              : fromDate
                ? { before: fromDate }
                : toDate
                  ? { after: toDate }
                  : undefined
          }
        />
      </PopoverContent>
    </Popover>
  );
}
